import { api } from '../lib/api-client'
import { API_PREFIX, endpoints } from '../lib/endpoints'
import { getRefreshToken, setTokens } from './token-storage'

/**
 * A super administrator opening another organisation with its administrator's rights.
 *
 * The server mints a session scoped to that organisation (`POST /auth/impersonate`,
 * audited). The super administrator's OWN session is kept aside, untouched, so
 * "Revenir à la plateforme" puts it back instead of asking for a new login. The page
 * is reloaded on each switch: every cached screen belongs to the organisation it was
 * loaded for, and a full reload is the one way to be sure none of it leaks across.
 */

const PLATFORM_REFRESH_KEY = 'fleetcore.platform.rt'
const OPENED_ORGANISATION_KEY = 'fleetcore.platform.org'

export const impersonateEndpoint = `${API_PREFIX}/auth/impersonate`

type Go = (path: string) => void
const reload: Go = (path) => window.location.assign(path)

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* Storage denied: the switch then lasts as long as the tab. */
  }
}

/** The organisation currently opened by a super administrator, or null. */
export function openedOrganisation(): string | null {
  return read(PLATFORM_REFRESH_KEY) === null ? null : read(OPENED_ORGANISATION_KEY)
}

export async function openOrganisation(
  tenantId: string,
  name: string,
  go: Go = reload,
): Promise<void> {
  const response = await api.post<{ accessToken: string; refreshToken: string }>(
    impersonateEndpoint,
    { tenantId },
  )
  // Only the FIRST switch keeps the original session: going from SGLTR straight to
  // another organisation must still come back to the super administrator's own.
  if (read(PLATFORM_REFRESH_KEY) === null) write(PLATFORM_REFRESH_KEY, getRefreshToken())
  else {
    // The organisation being left: its session is closed rather than abandoned.
    const left = getRefreshToken()
    void api
      .post(endpoints.logout, { refreshToken: left }, { skipAuthRefresh: true })
      .catch(() => undefined)
  }
  write(OPENED_ORGANISATION_KEY, name)
  setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken })
  go('/dashboard')
}

export async function returnToPlatform(go: Go = reload): Promise<void> {
  const original = read(PLATFORM_REFRESH_KEY)
  const opened = getRefreshToken()
  try {
    await api.post(endpoints.logout, { refreshToken: opened }, { skipAuthRefresh: true })
  } catch {
    /* The organisation's session ends locally either way. */
  }
  write(PLATFORM_REFRESH_KEY, null)
  write(OPENED_ORGANISATION_KEY, null)
  setTokens({ accessToken: '', refreshToken: original })
  go('/platform')
}

/**
 * On sign-out while an organisation is open, the kept-aside session is closed too:
 * otherwise the super administrator's own session would outlive the logout.
 */
export async function forgetPlatformSession(): Promise<void> {
  const original = read(PLATFORM_REFRESH_KEY)
  write(PLATFORM_REFRESH_KEY, null)
  write(OPENED_ORGANISATION_KEY, null)
  if (original === null) return
  try {
    await api.post(endpoints.logout, { refreshToken: original }, { skipAuthRefresh: true })
  } catch {
    /* Ignored, as for every logout. */
  }
}
